// CI-wait policy (plan 101 doc 05 step 4) — the ONE place the three-way split lives.
//
// Waiting for someone else's CI has exactly three ways to end badly, and reading any
// two of them as one is a product bug:
//
//   **failure**  a check went red     → the bounded CI-fix lane, then block
//   **timeout**  CI never concluded   → a human, never a merge
//   **no checks** nothing registered  → "not yet" for a grace, only then "not ever"
//
// Read together they produce the classic auto-merge holes: an empty check set read as
// green merges a PR before a single job starts; a hung CI read as "no CI" merges an
// ungated diff; a red check read as a timeout hands a fixable failure to a human (and
// the mirror, a timeout read as a failure, burns the fix budget on a diff nobody
// broke). {@link classifyCiWait} is the single ladder that keeps them apart, and every
// lane that grades a head's CI reads it:
//
// | Lane | Reads |
// |------|-------|
// | babysit (mothership) | `decideBabysit` (`./babysit`) grades an empty set's registration grace through {@link classifyCiWait}, and a running check's ceiling with {@link ciWaitExceeded} on its ONE clock, our own hop history (#6633); `apps/worker/src/babysit/observe.ts` asks {@link ciWaitNeedsHeadAge} whether a time gate can even fire before it spends the `/commits/:sha` round-trip |
// | review (relevance, deference) | `@developerz/review` `reviewRelevance` and `apps/worker` `readDeferenceCi` grade a head's CI through {@link classifyCiWait}, anchored on when THEIR lane first saw it — the only callers whose `timed_out` grades a running set |
// | the box | `apps/runner/src/tools/ci-fix.ts` grades the head it staged failed logs for through the SAME ladder, so a zero-run head reads as "CI has not started", never as "no failed CI at this commit" |
//
// `guardedMerge` (`packages/review/src/merge-gate/guarded-merge.ts`, its pure ladder
// `mergeGateForLiveState` in `gate.ts`) stays the authoritative merge
// egress and is untouched by this module: it re-reads the live head and re-asserts
// {@link ciReadyToMerge} + `protectionsMet` one read later. That is precisely why the
// predicates live here — the decision and the egress must grade a head identically,
// and they can only be guaranteed to if there is one implementation to disagree with.
//
// ## The policy, and where each row is realized
//
// | Situation | Rule | Realized by |
// |---|---|---|
// | no checks yet | `CHECKS_START_WAIT_MS` (60s) before an empty set means anything, then `CHECKS_EMPTY_GRACE_MS` (60s) of still-empty before it means "genuinely no checks" | {@link CHECKS_EMPTY_SETTLED_MS} + {@link emptyChecksSettled} |
// | failure | → the CI-fix lane, budgeted attempts (`DEFAULT_MAX_CI_FIX_ATTEMPTS`), then block — but only once the head has SETTLED: a red check beside a still-running one waits (#3883) | `decideBabysit`'s settle gate (`./babysit/settle`), then {@link classifyCiWait}'s `failed` |
// | timeout | hard ceiling 120 min, then a human | {@link CI_WAIT_CEILING_MS} + {@link ciWaitExceeded} |
// | force-advance | only on admin/force intent — **never** past a real failure | structural, see below |
// | review-bot grace | 2 min after CI first goes green, before an unreviewed head may merge — and only where the repo opted out of `pr.require_review` (#1080) | {@link REVIEW_SETTLEMENT_MS} + {@link reviewSettled} |
// | review wait bound | the same 120 min, on a watch that has not CHANGED — the bound for a wait whose head could not be dated | {@link reviewWaitExceeded} |
// | transient read failure | tolerate up to {@link CI_CHECK_READ_TOLERANCE} consecutive bad reads | the sweep, see below |
// | poll cadence | 1s → 60s backoff under the ceiling | **not ours** — neither lane polls, see below |
//
// **Force-advance is not a code path, and that is the encoding.** Nothing here can
// turn a wait into an advance: `failed` is graded BEFORE `timed_out` in the ladder, so
// a red head can never time out into anything, and `timed_out` maps to `escalate` in
// the only consumer that acts on it. The sole merge egress re-asserts
// {@link ciReadyToMerge}, which is false for every non-`ready` outcome. So forcing past
// a stuck wait is an ADMIN act performed on GitHub with admin rights, outside this
// ladder — there is no automatic path to it, and adding a `force` flag here would be
// building the hole this module exists to close.
//
// **Neither lane polls, so there is no backoff to configure.** The box is RELEASED
// when the PR opens (waiting must never hold a VM captive), and the mothership drives
// the PR off webhooks plus a fixed ~7-min reconcile sweep. So aitm's `1s → 60s`
// poll-loop backoff has no counterpart here and is deliberately NOT exported as a
// number nothing reads. Its invariant survives in the form that matters:
// {@link CI_CHECK_READ_TOLERANCE} consecutive faulted observations must still fit
// inside {@link CI_WAIT_CEILING_MS}, so a run of bad reads escalates as a TIMEOUT
// (with a human told) long before it could be mistaken for a verdict. That is
// guard-tested against the real sweep cadence in
// `apps/worker/src/cron/babysit-sweep.test.ts`.
//
// Pure: total functions over a `CiState`, a required-check list and a head age in ms.
// No clock (the observe seam owns it), no I/O, no policy read.

/**
 * Rolled-up CI state of a head's checks (`rollupCi`, `./babysit`). `none` = no checks
 * reported — whether that is "not yet" or "not ever" is NOT this rollup's call: it is
 * decided by the head's age against the registration grace ({@link emptyChecksSettled}).
 */
export type CiState = 'passing' | 'failing' | 'pending' | 'none';

// ─── empty-check grace (not-yet-registered ≠ no-CI) ───────────────────────────
//
// A push does not register its checks instantly: GitHub answers `check-runs` with an
// empty list for the first seconds of a commit's life. An empty set has nothing
// failing and nothing pending, so reading it literally says "green" — and a just-
// pushed PR would auto-merge before a single job starts. The fix is a GRACE anchored
// on the head commit's age: an empty set is `pending` until the head is old enough
// that CI would have registered by now; only then does it mean "this repo has no CI".
//
// Semantics ported (re-implemented, not vendored) from the sibling `ai-task-master`
// poll loop, which spends the same two waits before it accepts an empty set: one
// before the first poll (checks aren't there yet) and one of continuously-empty
// polling after it. We have no poll loop — the driver is webhook/cron-driven — so the
// two waits collapse into ONE window measured from the commit, and the sum is what
// carries over.

/** Wait after a push before an empty check set means anything at all. */
export const CHECKS_START_WAIT_MS = 60_000;
/** Further window an already-empty check set must stay empty before it is believed. */
export const CHECKS_EMPTY_GRACE_MS = 60_000;
/** Total head age at which an empty check set is finally read as "no CI configured". */
export const CHECKS_EMPTY_SETTLED_MS = CHECKS_START_WAIT_MS + CHECKS_EMPTY_GRACE_MS;

/**
 * Has an empty check set been empty long enough to mean "this repo has no CI" rather
 * than "CI hasn't registered yet"? Fails CLOSED: an unknown / non-finite / negative
 * age (`null` from a seam that couldn't date the head, a clock skewed behind the
 * commit) is NOT settled — never merge on a grace we can't prove elapsed.
 */
export function emptyChecksSettled(headAgeMs: number | null | undefined): boolean {
  if (headAgeMs == null || !Number.isFinite(headAgeMs)) return false;
  return headAgeMs >= CHECKS_EMPTY_SETTLED_MS;
}

/**
 * How long until {@link emptyChecksSettled} turns true for this head: the rest of the grace,
 * or `null` when the head's age is unknown. For a repo with NO CI the grace ends on this
 * clock alone, with no check event to wake the watch, so the babysit pass re-arms for it
 * (`recheckAfterMs`, #7939). Unknown age is no clock: that wait ends on an event or the
 * reconciler, never on a guessed delay.
 */
export function emptyChecksGraceRemainingMs(headAgeMs: number | null | undefined): number | null {
  if (headAgeMs == null || !Number.isFinite(headAgeMs) || headAgeMs < 0) return null;
  return Math.max(0, CHECKS_EMPTY_SETTLED_MS - headAgeMs);
}

/**
 * Default head age at which an advisory review bot (CodeRabbit) is assumed to have
 * had its window to post after a green head lands (#912). A no-review-gate repo
 * (`review: 'none'`) waits this long before merging on green, so an advisory bot
 * that hasn't posted YET isn't merged past. A repo with a real review verdict
 * ('approved' / 'changes_requested' — or any verdict of OURS on file, including a
 * neutral `comment`) needs no grace: a verdict was already filed.
 *
 * Since #1080 the grace is no longer a licence to merge afterwards. Clearing it only
 * ENDS the wait; whether an unreviewed head may then merge is `pr.require_review`'s
 * call, and by default it may not — the grace is the advisory bot's window, never
 * evidence that a review happened.
 *
 * Anchored on the HEAD's age, so it is spent once per head rather than restarted by
 * every sweep: a re-derive two minutes later reads the same clock and merges. A new
 * push earns a fresh window on purpose — a new commit is a new thing to review.
 */
export const REVIEW_SETTLEMENT_MS = 120_000;

/**
 * Has a no-review-gate head aged past the {@link REVIEW_SETTLEMENT_MS} advisory-bot
 * window? Fails CLOSED, like {@link emptyChecksSettled}: an unknown / non-finite age
 * never clears the grace — never merge before a review bot could have posted on a
 * head we can't date.
 */
export function reviewSettled(headAgeMs: number | null | undefined): boolean {
  if (headAgeMs == null || !Number.isFinite(headAgeMs)) return false;
  return headAgeMs >= REVIEW_SETTLEMENT_MS;
}

/**
 * Is CI green enough to gate a merge? `passing` always; `none` only when no checks are
 * required AND the head has aged past {@link CHECKS_EMPTY_SETTLED_MS} (a repo with no
 * CI — nothing to be green — as opposed to a push whose checks haven't registered).
 * `pending`/`failing` never.
 *
 * `headAgeMs` is deliberately REQUIRED, not defaulted: every call site must state what
 * it knows about the head's age, so a new one cannot silently re-open the merge-before-
 * CI-registers hole.
 */
export function ciReadyToMerge(
  ci: CiState,
  requiredChecks: readonly string[],
  headAgeMs: number | null,
): boolean {
  if (ci === 'passing') return true;
  return ci === 'none' && requiredChecks.length === 0 && emptyChecksSettled(headAgeMs);
}

// ─── CI wait ceiling (a wait is bounded, not forever) ─────────────────────────
//
// Every other babysit bound is a COUNT (`maxCiFixAttempts`, `maxSweeps`), and neither
// bounds a PASSIVE wait: `wait` mints nothing, so a PR whose CI hangs — a job queued
// behind an exhausted runner pool, a required check that never registers, a workflow
// that silently never reports — parks in `awaiting_ci` and is re-derived by the sweep
// forever, with nobody told. The ceiling is the time bound that closes it.
//
// 120 minutes is the product requirement (a legitimate CI run may take that long, and
// nothing may give up on / mis-read a PR inside it) and matches the budget the sibling
// `ai-task-master` poll loop spends before it gives up (`CHECKS_TIMEOUT_MS`) — the same
// semantics re-implemented, not vendored, like the empty-check grace above.
//
// Distinct from the runner's on-box CI EXECUTION budget (`apps/runner`
// `DEFAULT_CI_TIMEOUT_MS`, 6h): that bounds how long one job may RUN on our hardware;
// this bounds how long the mothership WAITS for someone else's CI to conclude. Neither
// derives from the other.

/** Hard ceiling on waiting for CI on ONE head before handing the PR to a human. */
export const CI_WAIT_CEILING_MS = 120 * 60_000;

/**
 * Consecutive faulted check READS a wait tolerates before the fault itself becomes the
 * thing a human is told about. Neither lane polls, so nothing counts these in memory —
 * the tolerance is realized by the reconcile sweep: a faulted observation costs exactly
 * one hop, the next tick re-derives from scratch, and {@link CI_WAIT_CEILING_MS} is the
 * backstop that turns a persistent fault into a `timed_out` escalation. The number is
 * therefore a BOUND on the sweep cadence, not a retry budget: this many consecutive
 * missed observations must still fit inside the ceiling, or a GitHub outage would look
 * like a hung CI run and a human would hear about it a full ceiling late. Guard-tested
 * against the real cadence in `apps/worker/src/cron/babysit-sweep.test.ts`.
 */
export const CI_CHECK_READ_TOLERANCE = 5;

/**
 * Has a wait for CI outrun {@link CI_WAIT_CEILING_MS}? The ANCHOR is the caller's. Babysit's
 * (`decideBabysit`'s `checks_running` hold) is ONE clock, read from its own hop history:
 * `BabysitObservation.ciWaitForMs`, "first pending since the last settle" (#6633). So a push,
 * or a re-run after the head settled (a PR body edit re-queues a workflow, #6602), earns a
 * fresh window, and a superseded same-head attempt, a queued run's start or a commit status
 * moves nothing. Only a failed hop read falls back to the head's age, which bounds every wait
 * on the head. The attempt clock #6602 used restarted on each superseded attempt and lost a
 * queued run's wait when it started, because GitHub overwrites `started_at` then.
 *
 * An unknown / non-finite / negative age is NOT exceeded — the mirror of
 * {@link emptyChecksSettled}, and for the same reason: an age we cannot prove may not
 * be acted on. There it means "don't merge yet"; here it means "don't escalate yet".
 * Both default to keep-watching.
 */
export function ciWaitExceeded(headAgeMs: number | null | undefined): boolean {
  if (headAgeMs == null || !Number.isFinite(headAgeMs)) return false;
  return headAgeMs >= CI_WAIT_CEILING_MS;
}

/**
 * Has a REVIEW wait outrun the same {@link CI_WAIT_CEILING_MS} budget (#1080)? Same
 * predicate, different ANCHOR: `watchIdleMs` is the time since the first babysit hop
 * that observed this head (`firstBabysitHopAtForHead`, plan 101 M9), not how old the
 * head is — because the branch that needs this bound is precisely the one where the
 * head could not be dated, so {@link REVIEW_SETTLEMENT_MS} can never be proved
 * elapsed and the wait would otherwise be forever with nobody told.
 *
 * Only a new head restarts the clock. A status change does not: the anchor used to be
 * the task row's `updated_at`, which every status CAS bumps, so a PR flapping between
 * two waits reset the bound on each swing and never escalated. Unknown is not
 * exceeded, like every other predicate here.
 */
export const reviewWaitExceeded = ciWaitExceeded;

// ─── the three-way split ──────────────────────────────────────────────────────

/**
 * How a head's CI wait stands right now — the ONE classification every lane reads.
 * Mutually exclusive by construction, so a consumer can never route one head down two
 * lanes, and `failure` / `timeout` / `no checks` are three distinct values rather than
 * three readings of one boolean.
 */
export type CiWaitOutcome =
  /** Green enough to gate a merge ({@link ciReadyToMerge}) — the only outcome that advances. */
  | { readonly kind: 'ready' }
  /** A check went red. The bounded CI-fix lane's, never the ceiling's — see {@link classifyCiWait}. */
  | { readonly kind: 'failed' }
  /** Not green, not red, and past {@link CI_WAIT_CEILING_MS}: the WAIT is now the problem. */
  | { readonly kind: 'timed_out' }
  /** Checks are registered and still running. */
  | { readonly kind: 'pending' }
  /** The check set is EMPTY — the two readings the grace tells apart. */
  | {
      readonly kind: 'no_checks';
      /**
       * Has the head aged past {@link CHECKS_EMPTY_SETTLED_MS}? `false` ⇒ "CI hasn't
       * registered yet" (and an unprovable age reads as `false`, never as settled).
       */
      readonly settled: boolean;
      /**
       * The repo REQUIRES branch-protection checks that have not registered. Such a
       * head can never become `ready` on the grace alone — only the required checks
       * actually reporting, or the ceiling, resolves it.
       */
      readonly requiredMissing: boolean;
    };

/**
 * Grade one head's CI wait into exactly one {@link CiWaitOutcome}. The LADDER is the
 * policy, and its order carries two guarantees:
 *
 *   1. `failed` is graded FIRST, so a red head is always the bounded fix lane's and can
 *      never be reported as a timeout — "never force-advance past a real failure" holds
 *      because a real failure never reaches the clock at all.
 *   2. `ready` is graded before the ceiling, so a wait that concluded green on its last
 *      possible read is merged, not escalated for having taken too long to say yes.
 *
 * `headAgeMs` is the caller's anchor, as an age (the caller dates it; this core stays
 * clock-free). `null`/unprovable fails closed in BOTH directions — it never clears the
 * registration grace and never trips the ceiling, so an undateable head keeps watching
 * rather than merging early or escalating early.
 *
 * WHO reaches `timed_out` on a set still running (#6633 checked each caller): NOT babysit —
 * its settle gate holds a running set first (`gradeSettlement`'s `checks_running`) and calls
 * this ladder only on an EMPTY set, for the registration grace; its ceiling on a running set
 * is {@link ciWaitExceeded} on the hop clock, the ONE clock that wait has. NOT the box
 * (`apps/runner` `tools/ci-fix.ts`), which passes `null`. The review lane does:
 * `@developerz/review` `reviewRelevance` reviews a head still pending past the ceiling from
 * when it first saw it, disclosed `timed_out`; `apps/worker` `readDeferenceCi` reads it as
 * not confirmed. So the rung is live, and stays.
 */
export function classifyCiWait(input: {
  readonly ci: CiState;
  readonly requiredChecks: readonly string[];
  readonly headAgeMs: number | null | undefined;
}): CiWaitOutcome {
  const { ci, requiredChecks, headAgeMs } = input;
  if (ci === 'failing') return { kind: 'failed' };
  if (ciReadyToMerge(ci, requiredChecks, headAgeMs ?? null)) return { kind: 'ready' };
  if (ciWaitExceeded(headAgeMs)) return { kind: 'timed_out' };
  if (ci === 'none') {
    return {
      kind: 'no_checks',
      settled: emptyChecksSettled(headAgeMs),
      requiredMissing: requiredChecks.length > 0,
    };
  }
  return { kind: 'pending' };
}

/**
 * Can a time gate still decide this head's hop — i.e. is the head's AGE worth reading?
 * The observe seam pays a `/commits/:sha` round-trip for it, so it asks first; a settled
 * green head with a filed review verdict reads no clock and pays nothing.
 *
 * The three live gates, and nothing else: the registration grace and the wait ceiling
 * (an empty set, or one with a check still running or a required one absent — whatever
 * the rollup says), and the review-bot settlement grace (green, but no verdict filed
 * yet). Lives HERE rather than in the seam because it is a restatement of
 * which gates exist: a fourth gate added beside them without this predicate knowing
 * would silently stop being fed an age, and an unknown age fails closed — the gate would
 * simply never fire, with nothing to notice it.
 */
export function ciWaitNeedsHeadAge(
  ci: CiState,
  opts: {
    readonly reviewVerdictFiled: boolean;
    /**
     * Is ANY check on the head still running, or a required one still absent (#3883)?
     * The rollup cannot say: it grades a red check beside a running one `failing`,
     * and that head is WAITING now (the settle gate, `./babysit/settle`) — so the
     * ceiling must be able to fire on it, which means it must be fed an age.
     */
    readonly checksUnsettled: boolean;
  },
): boolean {
  if (opts.checksUnsettled) return true;
  if (ci === 'none' || ci === 'pending') return true;
  return ci === 'passing' && !opts.reviewVerdictFiled;
}
